import React from 'react';

interface FunnelStickyCTAProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /**
   * Disparado cuando el usuario pulsa la CTA en estado disabled. Mismo
   * patrón que FunnelLayout.onContinueDisabledAttempt en P1-P8: la pantalla
   * lo usa para marcar en rojo el campo que falta por rellenar.
   */
  onDisabledAttempt?: () => void;
  summary?: {
    top?: string;
    bottom?: string;
  };
  secondaryLabel?: string;
  onSecondaryClick?: () => void;
  onBack?: () => void;
  backLabel?: string;
}

/**
 * Barra siempre visible en el bottom del funnel con CTA principal.
 *
 * Pasamos de `position: sticky` a `position: fixed` porque sticky
 * dependía de que el contenedor padre tuviera `min-h-screen` real, y
 * en iPad / Safari el viewport encogía con la barra de URL y a veces
 * la CTA quedaba fuera de pantalla aunque hubiera contenido corto.
 * Fixed garantiza que la CTA siempre se ve. El padding inferior con
 * `safe-area-inset-bottom` reserva espacio para la home indicator de
 * iPhone / iPad. El padding del scrollable ya lo aporta el caller con
 * `pb-28`.
 */
export default function FunnelStickyCTA({
  label,
  onClick,
  disabled = false,
  onDisabledAttempt,
  summary,
  secondaryLabel,
  onSecondaryClick,
  onBack,
  backLabel = 'Atrás',
}: FunnelStickyCTAProps) {
  const hasSummary = summary && (summary.top || summary.bottom);

  // Trampa de API: si la pantalla deshabilita el CTA pero olvida el callback,
  // los clicks se silencian sin feedback (regresión vista en Step9 mid-PR).
  if (process.env.NODE_ENV !== 'production' && disabled && !onDisabledAttempt) {
    // eslint-disable-next-line no-console
    console.warn(
      '[FunnelStickyCTA] disabled=true sin onDisabledAttempt — los clicks se ignorarán sin feedback al usuario',
    );
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] z-20"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="funnel-sticky-cta"
    >
      <div className="max-w-3xl mx-auto px-4 py-3">
        {hasSummary && (
          <div className="text-center mb-2">
            {summary?.top && <div className="font-poppins text-[11px] text-[#888]">{summary.top}</div>}
            {summary?.bottom && <div className="font-fredoka text-lg font-bold text-[#1a1a1a]">{summary.bottom}</div>}
          </div>
        )}
        {/* Tipografía y peso unificados con FunnelLayout (P1-P8): mismo
            text-lg + font-medium en ambos botones. Antes Atrás iba con
            border-gris y font-semibold, y Continuar con font-bold/text-sm
            — Santiago: "el formato del texto Atrás/Continuar tiene que ser
            exactamente igual en todas las pantallas". */}
        {/* Layout: Atrás ocupa un ancho fijo y Continuar el resto. Ambos con
            whitespace-nowrap para que la flecha nunca quede colgada en una
            segunda línea. Esta estructura es la misma en todos los pasos
            de Fase 2 — los labels los aporta cada step pero el tamaño y
            posición son consistentes. */}
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="shrink-0 px-4 sm:px-6 py-3 rounded-xl font-fredoka text-base sm:text-lg font-medium text-[#008BBC] hover:bg-[#008BBC]/5 transition-all active:scale-[0.98] whitespace-nowrap"
            >
              {backLabel}
            </button>
          )}
          <button
            type="button"
            // No usamos `disabled` HTML para capturar clicks en estado
            // deshabilitado: cuando el usuario insiste, disparamos
            // onDisabledAttempt para que la pantalla marque el campo
            // pendiente con borde rojo (mismo patrón que FunnelLayout).
            onClick={() => {
              if (disabled) {
                onDisabledAttempt?.();
              } else {
                onClick();
              }
            }}
            aria-disabled={disabled}
            className={`flex-1 min-w-0 px-4 sm:px-6 py-3 rounded-xl font-fredoka text-base sm:text-lg font-medium transition-all whitespace-nowrap overflow-hidden text-ellipsis ${
              disabled
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#008BBC] text-white shadow-md active:scale-[0.98] hover:bg-[#007aa8]'
            }`}
          >
            {label}
          </button>
        </div>
        {secondaryLabel && onSecondaryClick && (
          <div className="text-center mt-1.5">
            <button
              type="button"
              onClick={onSecondaryClick}
              className="font-poppins text-[11px] text-[#aaa] hover:text-[#666] transition-colors"
            >
              {secondaryLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
